import React, { useState, useEffect } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { TableSortDirection } from '@amzn/meridian/table/table';
import SearchField, {
  SearchSuggestionHeader,
  SearchSuggestion,
  SearchSuggestionFooter,
} from '@amzn/meridian/search-field';
import Pagination from '@amzn/meridian/pagination';
import { FormattedMessage, useIntl } from 'react-intl';
import styles from './ViewStyle.module.css';
import {
  DIRECT_REPORT_TYPE,
  SortType,
  TeamDetailsMetricsPerUser,
  TrendAndDetailsMetrics,
} from './types';
import { SortDirection } from '../../../../Types/SortDirection';
import { getMetricStringIdAndDefaultMessage } from '../LighthouseMetrics/helper';
import { DisplayMetric } from './DisplayMetric';
import Box from '@amzn/meridian/box';
import Select, { SelectOption } from '@amzn/meridian/select';
import { TeamProfile } from '../../../../Types/TeamProfile';
import {
  computeColumnWidthAsPercent,
  findDateLabel,
  findFullNameFromIdWithPrefix,
  getDirectReportViewSubtitleMessage,
  getReportingTeamForDirectReports,
  transformTeamDetailsData,
} from './helper';
import { TrendCategory } from '@amzn/aet-galactus-kit';
import RadioButton from '@amzn/meridian/radio-button';
import useInspireOptions from '../../../../hooks/useInspireOptions';

export interface Trend {
  readonly name: string;
  readonly trendDatapoint: TrendDatapoint[];
}

export interface TrendDatapoint {
  readonly timestamp: string;
  readonly datapoint: string | null;
  readonly networkAverage: string | null;
}

const filterTeamDetailsByTimestamp = (
  data: TrendAndDetailsMetrics[],
  timestamp: string
): TrendAndDetailsMetrics[] => {
  return data.map((user: TrendAndDetailsMetrics) => ({
    ...user,
    metricDatas: {
      ...user.metricDatas,
      trend: user.metricDatas?.trend
        ?.map((metric: Trend) => ({
          ...metric,
          trendDatapoint: metric.trendDatapoint.filter(
            (datapoint: TrendDatapoint) => datapoint.timestamp === timestamp
          ),
        }))
        .filter((metric: Trend) => metric.trendDatapoint.length > 0),
    },
  }));
};

interface CSAViewProps {
  dateSelection: string;
  showNetworkAverage: boolean;
  teamProfile: TeamProfile;
  dateList: string[];
  teamDetailsMetricsData: TrendAndDetailsMetrics[];
  metricType: string[];
  subMetricType: string[];
  verticalValue: string;
  marketplaceValue: string;
  skillValue: string;
  channelValue: string;
  segmentValue: string;
  metricCategory: string;
  clickstream?: any;
  customDate: boolean;
}

const itemsPerPage = 10;
const CSAView: React.FC<CSAViewProps> = ({
  dateSelection,
  teamProfile,
  dateList,
  teamDetailsMetricsData,
  metricType,
  subMetricType,
  metricCategory,
  clickstream,
  customDate,
}): React.ReactElement => {
  const intl = useIntl();
  const { isOm } = useInspireOptions();
  const directReportType = isOm
    ? DIRECT_REPORT_TYPE.TM
    : DIRECT_REPORT_TYPE.ASSOCIATE;
  const [tableData, setTableData] = useState<
    Record<string, Record<string, string>>
  >({});
  const [sortColumn, setSortColumn] = useState('day');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>(
    SortDirection.ASCENDING
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [metricTypes, setMetricTypes] = useState<string[]>();
  const [csaDateSelection, setCsaDateSelection] = useState<string>();
  const [checked, setChecked] = useState(true);
  const [csaList, setCsaList] = useState<
    { userId: string; displayName: string }[]
  >([]);
  const [query, setQuery] = useState('');
  const [suggestionFilter, setSuggestionFilter] = useState('');
  const [userFilter, setUserFilter] = useState<string>('');
  const [sortType, setSortType] = useState<SortType>('metric');

  const nameColumn = intl.formatMessage({
    id: 'cs_perfportal_search_results_name_column',
    defaultMessage: 'Name',
  });

  const onSearchSubmit = (search: string) => {
    if (search === undefined) {
      search = '';
      setUserFilter('');
    }
    setSuggestionFilter(search);
  };
  const onSuggestionClick = (selectedUserName: string) => {
    if (selectedUserName == userFilter) {
      setUserFilter('');
      setQuery('');
    } else {
      setUserFilter(selectedUserName);
      setQuery(selectedUserName);
    }
  };

  const firstVisibleIndex = (currentPage - 1) * itemsPerPage;
  const lastVisibleIndex = firstVisibleIndex + itemsPerPage;

  const onSort = ({
    sortColumn,
    sortDirection,
  }: {
    sortColumn: string;
    sortDirection: TableSortDirection;
  }) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  };

  const findUserIdByName = (name: string): string => {
    const user = csaList.find(user => user.displayName === name);
    return user ? user.userId : '';
  };

  useEffect(() => {
    if (teamProfile) {
      const sortedUserList = getReportingTeamForDirectReports(
        teamProfile.reportingTeam
      )
        .map(user => ({
          userId: user.relatedUser.userID,
          displayName: user.relatedUser.displayName,
        }))
        .filter(
          user =>
            suggestionFilter === '' ||
            user.displayName
              .toLowerCase()
              .includes(suggestionFilter.toLowerCase())
        )
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
      setCsaList(sortedUserList);
    }
  }, [teamProfile, suggestionFilter]);

  useEffect(() => {
    if (dateList) {
      setCsaDateSelection(dateList[0]);
    }
  }, [dateSelection, dateList]);

  useEffect(() => {
    if (
      !teamDetailsMetricsData ||
      !teamDetailsMetricsData[0] ||
      !teamDetailsMetricsData[0].metricDatas?.trend
    ) {
      return;
    }

    setMetricTypes(teamDetailsMetricsData[0].metricTypes);

    if (csaDateSelection) {
      const filteredTeamDetailsMetrics = filterTeamDetailsByTimestamp(
        teamDetailsMetricsData,
        csaDateSelection
      );

      if (!filteredTeamDetailsMetrics) {
        return;
      }

      const dataPerUser: TeamDetailsMetricsPerUser = transformTeamDetailsData(
        filteredTeamDetailsMetrics
      );

      const userKeys = Object.keys(dataPerUser);
      if (sortColumn === nameColumn) {
        userKeys.sort((a: string, b: string) => {
          return sortDirection === SortDirection.ASCENDING
            ? findFullNameFromIdWithPrefix(a, teamProfile).localeCompare(
                findFullNameFromIdWithPrefix(b, teamProfile)
              )
            : findFullNameFromIdWithPrefix(b, teamProfile).localeCompare(
                findFullNameFromIdWithPrefix(a, teamProfile)
              );
        });
      } else {
        userKeys.sort((a, b) => {
          let valA, valB;

          if (sortType === 'metric') {
            valA = dataPerUser[a][sortColumn] || '';
            valB = dataPerUser[b][sortColumn] || '';
          } else {
            valA = dataPerUser[a][sortColumn + 'BenchmarkDiff'] || '';
            valB = dataPerUser[b][sortColumn + 'BenchmarkDiff'] || '';
          }

          valA = valA.replace(/:/g, '.');
          valB = valB.replace(/:/g, '.');

          const numA = parseFloat(valA);
          const numB = parseFloat(valB);

          const isNumA = !isNaN(numA);
          const isNumB = !isNaN(numB);

          let comparison = 0;

          if (isNumA && isNumB) {
            comparison = numA - numB;
          } else {
            comparison = valA.localeCompare(valB);
          }

          if (sortDirection === 'descending') {
            comparison = -comparison;
          }
          return comparison;
        });
      }

      // filter per user data
      const filteredDataPerUser: TeamDetailsMetricsPerUser = {};
      userKeys.forEach(key => {
        if (userFilter === '' || key.includes(findUserIdByName(userFilter))) {
          filteredDataPerUser[key] = dataPerUser[key];
        }
      });

      // sort per user data
      const sortedDataPerUser: TeamDetailsMetricsPerUser = {};
      userKeys.forEach(key => {
        sortedDataPerUser[key] = filteredDataPerUser[key];
      });
      setTotalPages(
        Math.ceil(Object.keys(filteredDataPerUser).length / itemsPerPage)
      );
      setTableData(filteredDataPerUser);
    }
  }, [
    sortDirection,
    sortColumn,
    checked,
    csaDateSelection,
    dateList,
    suggestionFilter,
    userFilter,
    metricType,
    subMetricType,
    sortType,
  ]);

  return (
    <>
      <Row spacingInset='300' data-cy='csa-view-title'>
        {metricCategory === TrendCategory.COMPLIANCE ? (
          <Text type='h200'>
            <FormattedMessage
              id='cs_perfportal_metrics_compliance_metrics'
              defaultMessage='{directReportType} Compliance Metrics'
              values={{ directReportType }}
            />
          </Text>
        ) : metricCategory === TrendCategory.OTHER ? (
          <Text type='h200'>
            <FormattedMessage
              id='cs_perfportal_metrics_other_metrics'
              defaultMessage='{directReportType} Other Metrics'
              values={{ directReportType }}
            />
          </Text>
        ) : metricCategory === TrendCategory.EFFICIENCY ? (
          <Text type='h200'>
            <FormattedMessage
              id='cs_perfportal_metrics_efficiency_metrics'
              defaultMessage='{directReportType} Efficiency Metrics'
              values={{ directReportType }}
            />
          </Text>
        ) : metricCategory === TrendCategory.QUALITY ? (
          <Text type='h200'>
            <FormattedMessage
              id='cs_perfportal_metrics_quality_metrics'
              defaultMessage='{directReportType} Quality Metrics'
              values={{ directReportType }}
            />
          </Text>
        ) : (
          <Text type='h200'>
            <FormattedMessage
              id='cs_perfportal_metrics_nothing_to_show'
              defaultMessage='Nothing to show.'
            />
          </Text>
        )}
      </Row>
      <Row width='100%' widths='fill' spacingInset='300'>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <Text type='b200'>
              {getDirectReportViewSubtitleMessage(
                dateSelection,
                directReportType,
                customDate
              )}
            </Text>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div>
              {' '}
              <Text type='b300'>
                <FormattedMessage
                  id='cs_perfportal_network_benchmark'
                  defaultMessage='Network Benchmark'
                />
              </Text>
            </div>
            <div>
              {' '}
              <Toggle checked={checked} onChange={setChecked}>
                {checked}
              </Toggle>
            </div>
          </div>
        </div>
      </Row>
      <Row width='100%' widths='fill' spacingInset='300'>
        <Box spacingInset='8' type='fill' className={styles.flexcontainer}>
          <Row width='300px'>
            <SearchField
              value={query}
              onChange={setQuery}
              placeholder={intl.formatMessage({
                id: 'cs_perfportal_search_for',
                defaultMessage: 'Search for...',
              })}
              onSubmit={onSearchSubmit}
              onClear={onSearchSubmit}
              searchButton={true}
              data-cy='search-for'
            >
              <SearchSuggestionHeader>
                {intl.formatMessage({
                  id: 'cs_perfportal_search_suggestions',
                  defaultMessage: 'Search Suggestions',
                })}
              </SearchSuggestionHeader>
              {csaList.map(user => (
                <SearchSuggestion onClick={onSuggestionClick}>
                  {user.displayName}
                </SearchSuggestion>
              ))}
              <SearchSuggestionFooter>
                <Row alignmentHorizontal='justify'>
                  <Text>
                    {intl.formatMessage(
                      {
                        id: 'cs_perfportal_search_result_count',
                        defaultMessage: `{0} Results`,
                      },
                      { 0: csaList.length }
                    )}
                  </Text>
                </Row>
              </SearchSuggestionFooter>
            </SearchField>
          </Row>
          <Row alignmentHorizontal='justify' width='300px'>
            <Column heights='fill' alignmentVertical='center' height={72}>
              <Text type='h100'>
                <FormattedMessage
                  id='cs_perfportal_metric_sort_by'
                  defaultMessage='Sort by'
                />
              </Text>
            </Column>
            <Column alignmentVertical='top'>
              <RadioButton
                checked={sortType === 'metric'}
                onChange={setSortType}
                name='sortType'
                value='metric'
              >
                <FormattedMessage
                  id='cs_perfportal_sort_metric_by_metric_value'
                  defaultMessage='Metric Value'
                />
              </RadioButton>
              <RadioButton
                checked={sortType === 'benchmark_diff'}
                onChange={setSortType}
                name='sortType'
                value='benchmark_diff'
              >
                <FormattedMessage
                  id='cs_perfportal_bq_benchmark_difference'
                  defaultMessage='Benchmark Difference'
                />
              </RadioButton>
            </Column>
          </Row>
          <div style={{ width: '300px' }}>
            <Select
              label={intl.formatMessage({
                id: 'cs_perfportal_metrics_view_by_date',
                defaultMessage: 'View By Date',
              })}
              value={csaDateSelection}
              width={288}
              key='CsaDate'
              data-cy='CsaDate'
              onChange={setCsaDateSelection}
            >
              {dateList.map(timestampItem => (
                <SelectOption
                  label={findDateLabel(timestampItem)}
                  value={timestampItem}
                  key={`${timestampItem}`}
                  {...clickstream}
                  data-csa-c-content-id='metrics-timestamp-select'
                  data-csa-c-type='select'
                  data-csa-c-tag3={timestampItem}
                />
              ))}
            </Select>
          </div>
        </Box>
      </Row>
      <Column
        width='100%'
        alignmentHorizontal='end'
        spacing='300'
        spacingInset='300'
      >
        <Table
          headerRows={1}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
          showDividers={true}
          spacing='small'
        >
          <TableRow>
            <TableCell
              sortColumn={nameColumn}
              width='15%'
              alignmentVertical='bottom'
            >
              {nameColumn}
            </TableCell>
            {metricTypes?.map(metric => (
              <TableCell
                sortColumn={metric}
                key={metric}
                width={computeColumnWidthAsPercent(metricTypes?.length)}
                alignmentVertical='bottom'
                alignmentHorizontal='center'
              >
                <FormattedMessage
                  {...getMetricStringIdAndDefaultMessage(metric)}
                />
              </TableCell>
            ))}
          </TableRow>
          {Object.entries(tableData)
            .slice(firstVisibleIndex, lastVisibleIndex)
            .map(([userId]) => (
              <TableRow key={userId} data-cy='table-row-content'>
                <TableCell>
                  <Text type='b300' data-testid={userId}>
                    {findFullNameFromIdWithPrefix(userId, teamProfile)}
                  </Text>
                  {checked && (
                    <>
                      <Text type='b200' color='secondary'>
                        <FormattedMessage
                          id='cs_perfportal_network_benchmark'
                          defaultMessage='Network Benchmark'
                        />
                      </Text>
                    </>
                  )}
                </TableCell>
                {metricTypes?.map(metric => (
                  <TableCell key={metric}>
                    <DisplayMetric
                      metric={tableData[userId]?.[metric]}
                      textType='b300'
                      metricType={metric}
                    ></DisplayMetric>
                    {checked && (
                      <>
                        <DisplayMetric
                          metric={tableData[userId]?.[`${metric}NetAvg`]}
                          textType='b200'
                          styleName={styles.subdued}
                          metricType={metric}
                        ></DisplayMetric>
                      </>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
        </Table>
        <Pagination
          showSkipArrows={true}
          numberOfPages={totalPages}
          onChange={setCurrentPage}
          currentPage={currentPage}
        />
      </Column>
    </>
  );
};

export default CSAView;
